<template>
  <div ref="containerNode" id="mapbox"
    :style="{
      height: '100vh',
      width: '100vw',
      position: 'relative', // for absolutely positioned children
      overflow: 'hidden',
      touchAction: 'none', // Not supported in Safari :(
      msTouchAction: 'none',
      cursor: grabbing?'grabbing':'grab',
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none'
    }"
    @mousewheel="onWheel"
    @touchstart="onTouchStart"
    @touchend="onTouchEnd"
    @touchmove="onTouchMove"
    @mousedown="onMouseDown"
    @mouseup="onMouseUp"
    @mousemove="onMouseMove"
    @dblclick="$emit('dblclick')"
  >
    <div
      :style="{
        display: 'inline-block', // size to content
        transform: transform,
        transformOrigin: '0 0 '
      }"
    >
    <slot/>
    </div>
  </div>
</template>

<script>
export default {
  name: 'mapInteraction',
  props: {
    value: {
      default: {
        scale: 1,
        translation: {
          x: 0,
          y: 0
        }
      }
    },
    maxScale: {
      default: 5
    },
    minScale: {
      default: 0.05
    },
    disableZoom: {
      default: false
    },
    disablePan: {
      default: false
    }
  },
  data () {
    return {
      grabbing: false,
      startMoving: false,
      passiveSupported: false,
      translationBounds: {},
      startPointerInfo: null
    }
  },
  computed: {
    transform () {
      return `translate(${this.value.translation.x}px, ${this.value.translation.y}px) scale(${this.value.scale})`
    }
  },
  methods: {
    onMouseDown (e) {
      this.grabbing = true
      e.preventDefault()
      this.setPointerState([e])
    },
    onMouseUp (e) {
      if (e.target.localName !== 'button') {
        if (!this.startMoving) {
        } else {
          const { layerX, layerY } = e
          this.$emit('tap2', { layerX, layerY })
        }
      } else {
        // console.log(e)
        this.$emit('tap', e.target.id)
      }
      this.setPointerState()
      this.grabbing = false
    },
    onMouseMove (e) {
      if (!this.startPointerInfo || this.disablePan) {
        return
      }
      this.startMoving = true
      e.preventDefault()
      this.onDrag(e)
    },
    onDrag (pointer) {
      const { translation, pointers } = this.startPointerInfo
      const startPointer = pointers[0]
      const dragX = pointer.clientX - startPointer.clientX
      const dragY = pointer.clientY - startPointer.clientY
      const newTranslation = {
        x: translation.x + dragX,
        y: translation.y + dragY
      }
      this.$emit('onChange', {
        scale: this.value.scale,
        translation: this.clampTranslation(newTranslation)
      })
    },
    scaleFromMultiTouch (e) {
      const startTouches = this.startPointerInfo.pointers
      const newTouches = e.touches
      // calculate new scale
      const dist0 = this.touchDistance(startTouches[0], startTouches[1])
      const dist1 = this.touchDistance(newTouches[0], newTouches[1])
      const scaleChange = dist1 / dist0
      const startScale = this.startPointerInfo.scale
      const targetScale = startScale + ((scaleChange - 1) * startScale)
      const newScale = this.clamp(this.props.minScale, targetScale, this.props.maxScale)
      // calculate mid points
      const startMidpoint = this.midpoint(this.touchPt(startTouches[0]), this.touchPt(startTouches[1]))
      const newMidPoint = this.midpoint(this.touchPt(newTouches[0]), this.touchPt(newTouches[1]))
      // The amount we need to translate by in order for
      // the mid point to stay in the middle (before thinking about scaling factor)
      const dragDelta = {
        x: newMidPoint.x - startMidpoint.x,
        y: newMidPoint.y - startMidpoint.y
      }
      const scaleRatio = newScale / startScale
      // The point originally in the middle of the fingers on the initial zoom start
      const focalPt = this.clientPosToTranslatedPos(startMidpoint, this.startPointerInfo.translation)
      // The amount that the middle point has changed from this scaling
      const focalPtDelta = {
        x: this.coordChange(focalPt.x, scaleRatio),
        y: this.coordChange(focalPt.y, scaleRatio)
      }
      // Translation is the original translation, plus the amount we dragged,
      // minus what the scaling will do to the focal point. Subtracting the
      // scaling factor keeps the midpoint in the middle of the touch points.
      const newTranslation = {
        x: this.startPointerInfo.translation.x - focalPtDelta.x + dragDelta.x,
        y: this.startPointerInfo.translation.y - focalPtDelta.y + dragDelta.y
      }
      this.$emit('onChange', {
        scale: newScale,
        translation: this.clampTranslation(newTranslation)
      })
    },
    onTouchMove (e) {
      if (!this.startPointerInfo) {
        return
      }
      this.startMoving = true
      e.preventDefault()
      const isPinchAction = e.touches.length === 2 && this.startPointerInfo.pointers.length > 1
      if (isPinchAction && !this.disableZoom) {
        this.scaleFromMultiTouch(e)
      } else if ((e.touches.length === 1) && this.startPointerInfo && !this.disablePan) {
        this.onDrag(e.touches[0])
      }
    },
    onTouchStart (e) {
      // console.log(e.touches, 'onTouchStart', e)
      e.preventDefault()
      this.setPointerState(e.touches)
    },
    onTouchEnd (e) {
      if (e.target.localName !== 'button') {
        if (!this.startMoving) {
          this.$emit('tap', e.touches)
        }
        // console.log(e.touches, 'onTouchEnd', e.target.localName)
        this.setPointerState(e.touches)
        this.startMoving = false
      }
    },
    setPointerState (pointers) {
      if (!pointers || pointers.length === 0) {
        this.startPointerInfo = undefined
        return
      }
      this.startPointerInfo = {
        pointers,
        scale: this.value.scale,
        translation: this.value.translation
      }
    },
    scaleFromPoint (newScale, focalPt) {
      const { translation, scale } = this.value
      const scaleRatio = newScale / (scale !== 0 ? scale : 1)
      const focalPtDelta = {
        x: this.coordChange(focalPt.x, scaleRatio),
        y: this.coordChange(focalPt.y, scaleRatio)
      }
      const newTranslation = {
        x: translation.x - focalPtDelta.x,
        y: translation.y - focalPtDelta.y
      }
      this.$emit('onChange', {
        scale: newScale,
        translation: this.clampTranslation(newTranslation)
      })
    },
    onWheel (e) {
      this.scaling = true
      this.scrollingUp = e.deltaY > 0
      e.preventDefault()
      e.stopPropagation()
      const scaleChange = 2 ** (e.deltaY * 0.002)
      const newScale = this.clamp(
        this.minScale,
        this.value.scale + (1 - scaleChange),
        this.maxScale
      )
      const mousePos = this.clientPosToTranslatedPos({ x: e.clientX, y: e.clientY })
      this.scaleFromPoint(newScale, mousePos)
      this.scaling = false
    },
    clampTranslation (desiredTranslation) {
      const { x, y } = desiredTranslation
      let { xMax, xMin, yMax, yMin } = this.translationBounds
      xMin = xMin !== undefined ? xMin : -Infinity
      yMin = yMin !== undefined ? yMin : -Infinity
      xMax = xMax !== undefined ? xMax : Infinity
      yMax = yMax !== undefined ? yMax : Infinity

      return {
        x: this.clamp(xMin, x, xMax),
        y: this.clamp(yMin, y, yMax)
      }
    },
    getContainerNode () { return this.$refs.containerNode },
    getContainerBoundingClientRect () {
      return this.getContainerNode().getBoundingClientRect()
    },
    translatedOrigin (translation = this.value.translation) {
      const clientOffset = this.getContainerBoundingClientRect()
      return {
        x: clientOffset.left + translation.x,
        y: clientOffset.top + translation.y
      }
    },
    clientPosToTranslatedPos ({ x, y }, translation = this.value.translation) {
      const origin = this.translatedOrigin(translation)
      return {
        x: x - origin.x,
        y: y - origin.y
      }
    },
    coordChange: (coordinate, scaleRatio) => {
      return (scaleRatio * coordinate) - coordinate
    },
    clamp (min, value, max) {
      return Math.max(min, Math.min(value, max))
    },
    distance (p1, p2) {
      const dx = p1.x - p2.x
      const dy = p1.y - p2.y
      return Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2))
    },
    midpoint (p1, p2) {
      return {
        x: (p1.x + p2.x) / 2,
        y: (p1.y + p2.y) / 2
      }
    },
    touchPt (touch) {
      return { x: touch.clientX, y: touch.clientY }
    },
    touchDistance (t0, t1) {
      const p0 = this.touchPt(t0)
      const p1 = this.touchPt(t1)
      return this.distance(p0, p1)
    }
  }
}
</script>

<style>

</style>
